import {AnalysisAttributes} from 'analyses/Analyses';
import {StandardDNABase} from 'biojs/BioConstants';
import {GapSequenceJSON} from 'biojs/GapSequence';
import {Side} from 'constants/MolbioConstants';
import * as SequenceAnalysisConstants from 'constants/SequenceAnalysisConstants';
import {ProteinVersion} from 'molbio/proteins/util/ProteinConstants';

import {ProteinAlignmentSummaryGraphQL} from '../../graphql-fragments/molbio/__generated/ProteinAlignmentSummaryGraphQL';
import {SequenceAnalysisSummaryGraphQL} from '../../graphql-fragments/molbio/__generated/SequenceAnalysisSummaryGraphQL';

export const ALIGNMENT_SCROLLING_PERF_INTERVAL = 30 * 1000;

export const AlignmentPreferences = {
  ALIGNMENT_PROGRAM: 'alignmentProgram',
  MAFFT_PARAMETERS: 'alignmentMafftParameters',
  CLUSTAL_O_PARAMETERS: 'alignmentClustalOParameters',
};

// Unfortunately DNA alignments use lowercase and Protein alignments use all caps
export enum DNAAlignmentKind {
  CONSENSUS = 'consensus',
  TEMPLATE = 'template',
}

// TODO: let's deprecate this.
export enum ProteinAlignmentKind {
  CONSENSUS = 'CONSENSUS',
  TEMPLATE = 'TEMPLATE',
}

// In the redesigned modal, template alignments can be created in bulk using either a pairwise or
// multisequence approach.
export enum BulkAlignmentCreationKind {
  PAIRWISE = 'PAIRWISE',
  MULTISEQUENCE = 'MULTISEQUENCE',
  CONSENSUS = 'CONSENSUS',
}

export enum AlignmentProgram {
  CLUSTALO = 'clustalo',
  MAFFT = 'mafft',
}

export enum MafftAdjustDirection {
  FAST = 'fast',
  ACCURATE = 'accurate',
  DISABLED = 'disabled',
}

export enum MafftStrategy {
  AUTO = 'auto',
  SIXMER = 'sixmer',
  LOCALPAIR = 'localpair',
  GLOBALPAIR = 'globalpair',
}

export type AlignmentProgramStrategy = MafftStrategy | AlignmentProgram.CLUSTALO;

export interface MafftParameters {
  maxIterations: number;
  retree: number;
  gapOpenPenalty: number;
  gapExtensionPenalty: number;
  adjustDirection: MafftAdjustDirection;
  strategy: MafftStrategy;
}

export interface ClustalOParameters {
  mbedGuideTree: boolean;
  mbedIteration: boolean;
  numCombinedIterations: number;
  maxGuidetreeIterations: number;
  maxHmmIterations: number;
}

export const MAFFT_DEFAULTS: MafftParameters = {
  maxIterations: 0,
  retree: 2,
  gapOpenPenalty: 1.53,
  gapExtensionPenalty: 0.0,
  adjustDirection: MafftAdjustDirection.FAST,
  strategy: MafftStrategy.AUTO,
};

export const CLUSTALO_DEFAULTS: ClustalOParameters = {
  mbedGuideTree: true,
  mbedIteration: true,
  numCombinedIterations: 0,
  maxGuidetreeIterations: -1,
  maxHmmIterations: -1,
};

export type DNAAlignmentRowType = 'aligned' | 'consensus' | 'template';

export enum AlignmentPolymerType {
  DNA = 'DNA',
  AA = 'AA',
}

export const RESIDUE_NAMES: {[k in AlignmentPolymerType]: string} = {
  DNA: 'base',
  AA: 'amino acid',
};

export const UNKNOWN_VALUE_BASES: {[k in AlignmentPolymerType]: string} = {
  DNA: 'n',
  AA: 'x',
};

export enum ReadingFrameName {
  FWD1 = 'FWD1',
  FWD2 = 'FWD2',
  FWD3 = 'FWD3',
}
export const READING_FRAME_NAMES = Object.values(ReadingFrameName) as Array<ReadingFrameName>;
export interface AminoAcidMap {
  [key: number]: string;
}
export enum TrimSideOption {
  START = 'start',
  END = 'end',
  BOTH = 'both',
}

export interface ProteinAlignmentRow {
  name: string;
  originalProteinId: string | null;
  proteinVersion: ProteinVersion;
}

export interface ProteinAlignment {
  alignmentType: ProteinAlignmentKind;
  createdAt: string;
  modifiedAt: string;
  id: string;
  name: string;
  proteinId: string;
  // We assume that the first one is the master and rely on the server to send them in the right order
  rows: Array<ProteinAlignmentRow>;
}

export interface AlignmentLocation {
  residueIndex: number;
  rowIndex: number;
}

export interface AlignmentSelectionPropsAttributes {
  start: number;
  end: number | null;
  anchor: Side;
  active: boolean;
  rowIndex: number;
}

export interface Trace {
  baseToColor: {[base in StandardDNABase]: Array<Array<number>>};
  peakLocations: Array<number | null>;
  qualityScores: Array<number | null>;
}
export interface DNAAlignmentJSONAttributes {
  sequence: GapSequenceJSON;
  trimStart: number;
  trimEnd: number;
  type: DNAAlignmentRowType;
  existingSequence: boolean;
  trace: Trace | null;
  reversed?: boolean | null;
  rotationIndex?: number | null;
}
export interface DNAAlignmentAnalysisJSONAttributes extends AnalysisAttributes {
  id: number;
  apiId: string;
  name: string;
  sequence_id: string;
  created_at: string;
  analysis_type: typeof SequenceAnalysisConstants.ALIGNMENT;
  alignments: Array<DNAAlignmentJSONAttributes>;
  alignmentType: DNAAlignmentKind;
  attachments: Array<object>;
  versionId: number;
  alignment_comments: Array<unknown>;
  attached_file_count: number;
}

export type AlignmentSummary = ProteinAlignmentSummaryGraphQL | SequenceAnalysisSummaryGraphQL;

export interface ParameterOptionValue<ValueType> {
  value: ValueType;
  display?: string;
  metadata?: string;
}

export interface ParameterOptions<ValueType> {
  label: string;
  irrelevantStrategies?: Array<AlignmentProgramStrategy>;
  options: Array<ParameterOptionValue<ValueType>>;
}

export type ParameterOptionValues<ParameterType> = {
  [param in keyof ParameterType]: ParameterOptions<ParameterType[param]>;
};

export const MAFFT_PARAMETER_OPTION_VALUES: Omit<ParameterOptionValues<MafftParameters>, 'strategy'> = {
  maxIterations: {
    label: 'Max refinement iterations',
    irrelevantStrategies: [MafftStrategy.AUTO],
    options: [
      {value: 0},
      {value: 1},
      {value: 2},
      {value: 10},
      {value: 25},
      {value: 50},
      {value: 75},
      {value: 100, metadata: 'slowest'},
    ],
  },
  retree: {
    label: 'Tree rebuilding',
    irrelevantStrategies: [MafftStrategy.AUTO, MafftStrategy.LOCALPAIR, MafftStrategy.GLOBALPAIR],
    options: [
      {value: 1, metadata: 'fastest'},
      {value: 2},
      {value: 3},
      {value: 5},
      {value: 10},
      {value: 25},
      {value: 50},
      {value: 75},
      {value: 100, metadata: 'slowest'},
    ],
  },
  gapOpenPenalty: {
    label: 'Gap open penalty',
    options: [
      {value: 1.0},
      {value: 1.1},
      {value: 1.2},
      {value: 1.3},
      {value: 1.4},
      {value: 1.53},
      {value: 1.6},
      {value: 1.7},
      {value: 1.8},
      {value: 1.9},
      {value: 2.0},
      {value: 2.1},
      {value: 2.2},
      {value: 2.3},
      {value: 2.4},
      {value: 2.5},
      {value: 2.6},
      {value: 2.7},
      {value: 2.8},
      {value: 2.9},
      {value: 3.0},
    ],
  },
  gapExtensionPenalty: {
    label: 'Gap extension penalty',
    options: [
      {value: 0.0},
      {value: 0.1},
      {value: 0.2},
      {value: 0.3},
      {value: 0.4},
      {value: 0.5},
      {value: 0.6},
      {value: 0.7},
      {value: 0.8},
      {value: 0.9},
      {value: 1.0},
      {value: 1.1},
      {value: 1.2},
      {value: 1.3},
      {value: 1.4},
      {value: 1.5},
      {value: 1.6},
      {value: 1.7},
      {value: 1.8},
      {value: 1.9},
      {value: 2.0},
    ],
  },
  adjustDirection: {
    label: 'Adjust direction',
    options: [
      {value: MafftAdjustDirection.ACCURATE, display: 'Enabled, accurately', metadata: 'Slow'},
      {value: MafftAdjustDirection.FAST, display: 'Enabled, quickly', metadata: 'Faster'},
      {value: MafftAdjustDirection.DISABLED, display: 'Disabled', metadata: 'Fastest'},
    ],
  },
};

export const CLUSTALO_PARAMETER_OPTION_VALUES: ParameterOptionValues<ClustalOParameters> = {
  mbedGuideTree: {
    label: 'mBed-like clustering guide-tree',
    options: [
      {value: true, display: 'Yes'},
      {value: false, display: 'No', metadata: 'slower'},
    ],
  },
  mbedIteration: {
    label: 'mBed-like clustering iteration',
    options: [
      {value: true, display: 'Yes'},
      {value: false, display: 'No', metadata: 'slower'},
    ],
  },
  numCombinedIterations: {
    label: 'Number of combined iterations',
    options: [{value: 0}, {value: 1}, {value: 2}, {value: 3}, {value: 4}, {value: 5, metadata: 'slowest'}],
  },
  maxGuidetreeIterations: {
    label: 'Max guide tree iterations',
    options: [
      {value: -1, display: 'No maximum'},
      {value: 0},
      {value: 1},
      {value: 2},
      {value: 3},
      {value: 4},
      {value: 5, metadata: 'slowest'},
    ],
  },
  maxHmmIterations: {
    label: 'Max HMM iterations',
    options: [
      {value: -1, display: 'No maximum'},
      {value: 0},
      {value: 1},
      {value: 2},
      {value: 3},
      {value: 4},
      {value: 5, metadata: 'slowest'},
    ],
  },
};
